import React, {useState, useRef, useEffect} from 'react';
import {View, TextInput, Platform, TouchableOpacity} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigation} from '@react-navigation/native';
import {Ionicons} from '@expo/vector-icons';
import i18n from 'i18n-js';
import styles from '../../style/scanpack';
import useWidth from '../scanToCartScanning/useWidth';
import ScanButtonContainer from '../scanpack/scanButtonContainer';
import {setScanPackBarcodeInput, setScanPackMainInputFocus, handleBarcodeSubmit, handlePassButton, handleScanAll} from '../../actions/scanpackScanningActions';
import useAutoFocus, {useNavigationFocus} from './hooks/useAutoFocus';
import useKeyboardSuppression, {getKeyboardSuppressionProps} from '../../hooks/useKeyboardSuppression';
import {useCameraScanner} from '../../helpers/useCameraScanner';
import CamScanner from '../camScanning/CamScanner';

const scanAllSuggestedItem = (order) => {
  const unscanned = order?.unscanned_items?.[0];
  if (!unscanned) return null;
  return unscanned.child_items?.length > 0 ? unscanned.child_items[0] : unscanned;
};

const scanAllNotReady = (order) => {
  const item = scanAllSuggestedItem(order);
  if (!item) return true;
  const scanned = item.scanned_qty || 0;
  const total = scanned + (item.qty_remaining || 0);
  return scanned <= 0 || total < 3;
};

const ScanPackBarcodeInput = ({popInternet = true}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [placeholder, setPlaceholder] = useState('');
  let bulkType = 'bulk_scan';
  const windowWidth = useWidth();

  const {barcodeValue, mainInputHasFocus, currentOrder, typeScanState, scanAllOption} = useSelector(state => ({
    barcodeValue: state.scanPackScanning.barcodeValue,
    mainInputHasFocus: state.scanPackScanning.mainInputHasFocus,
    currentOrder: state.scanPackScanning.currentOrder,
    typeScanState: state.scanPackScanning.typeScanState,
    scanAllOption: state.user?.bothSettings?.data?.data?.scanpack_setting?.scan_all_option || false
  }));

  const suppressSoftwareKeyboard = useKeyboardSuppression(inputRef);
  // 'next_items' is the old item screen's location, so a camera left open on order search stays open here.
  const navigation = useNavigation();
  const {showCameraScanner, camScannerEnabled, openCamera, closeCamera} = useCameraScanner('next_items', {navigation});

  useAutoFocus(100);
  useNavigationFocus();

  useEffect(() => {
    const initialPlaceholder = getPlaceHolder();
    setPlaceholder(initialPlaceholder);
  }, []);

  useEffect(() => {
    if (mainInputHasFocus) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  }, [mainInputHasFocus]);

  const getPlaceHolder = () => {
    return Platform.OS === 'web'
      ? i18n.t('nextItems.clickScanLabel') || 'Click to Scan'
      : i18n.t('nextItems.tabHereLabel') || 'Tab Here';
  };

  const handleBlur = () => {
    const blurValue = getPlaceHolder();
    setPlaceholder(blurValue);
    dispatch(setScanPackMainInputFocus(false));
  };

  const handleFocus = () => {
    const focusValue = i18n.t('nextItems.readyForScanLabel') || 'Ready For Product Scan';
    setPlaceholder(focusValue);
    dispatch(setScanPackMainInputFocus(true));
  };

  const handleBarcodeChange = (text) => {
    dispatch(setScanPackBarcodeInput(text));
  };

  const onBarcodeSubmit = () => {
    dispatch(handleBarcodeSubmit());
  };

  const handleCameraScan = (scannedValue) => {
    if (scannedValue) {
      dispatch(setScanPackBarcodeInput(scannedValue));
      dispatch(handleBarcodeSubmit());
    }
  };

  let cameraWidth = '100%';
  if (windowWidth > 900) {
    cameraWidth = '60%';
  } else if (windowWidth > 600) {
    cameraWidth = '80%';
  }

  const onPassPress = (item, isShiftKey = false) => {
    dispatch(handlePassButton(item, isShiftKey));
  };

  return (
    <LinearGradient
      start={{x: 0, y: 1}}
      end={{x: 0, y: 0}}
      colors={['#142130', '#304454']}
      style={styles.scanBarcodeGradientDesktop}>
      <View style={{flexDirection: 'row'}}>
        <View style={styles.scanBarcodeContainer}>
          <LinearGradient
            start={{x: 0, y: 1}}
            end={{x: 0, y: 0}}
            colors={['#6384ff', '#1329ff', '#35096c']}
            style={styles.scanBarcodeSubGradient}
          />
        </View>

        <View
          dataSet={{componentName: 'barcodeScan'}}
          style={styles.scanBarCodeInputContainer}>
          <TextInput
            testID="scanPackBarcodeInput"
            value={barcodeValue}
            ref={inputRef}
            style={[styles.scanBarCodeInput, camScannerEnabled && {paddingRight: 40}]}
            {...getKeyboardSuppressionProps(suppressSoftwareKeyboard)}
            onSubmitEditing={onBarcodeSubmit}
            onChangeText={handleBarcodeChange}
            placeholder={placeholder}
            placeholderTextColor={'#acacad'}
            onBlur={handleBlur}
            onFocus={handleFocus}
            autoCapitalize='none'
            returnKeyType='done'
          />
          {camScannerEnabled && (
            <TouchableOpacity
              testID="cameraScanBtn"
              style={{
                position: 'absolute',
                right: 10,
                top: 0,
                bottom: 0,
                justifyContent: 'center',
              }}
              onPress={openCamera}>
              <Ionicons
                name="camera"
                size={24}
                color="#4A6179"
              />
            </TouchableOpacity>
          )}
        </View>

        {currentOrder && currentOrder.unscanned_items && currentOrder.unscanned_items.length > 0 && (
          <ScanButtonContainer
            Order={currentOrder}
            popInternet={popInternet}
            scanAllItem={() => dispatch(handleScanAll(bulkType))}
            windowWidth={windowWidth}
            setType={(type) => { bulkType = type; }}
            checkCount={() => scanAllNotReady(currentOrder)}
            scanAllOption={scanAllOption}
            typeScanState={typeScanState}
            handlePassButton={onPassPress}
            isBarcodeEnter={true}
            setIsBarcodeEnter={() => {}}
          />
        )}
      </View>

      {camScannerEnabled && showCameraScanner && (
        <View style={{width: cameraWidth, alignSelf: 'center', marginTop: 10}}>
          <CamScanner
            inline={true}
            onScan={handleCameraScan}
            onClose={closeCamera}
          />
        </View>
      )}
    </LinearGradient>
  );
};

export default ScanPackBarcodeInput;

